import React, { useState } from 'react';
import { Modal, Button, Form, Input, message, Typography, Divider, Tabs } from 'antd';
import { WalletOutlined, UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAuth } from '../../contexts/AuthContext';

const { Text } = Typography;

const LoginModal = ({ visible, onClose, onSuccess }) => {
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [walletModalVisible, setWalletModalVisible] = useState(false);
  const { connected, publicKey, connect, wallet, wallets, select } = useWallet();
  const { loginWithWallet, loginWithCredentials, registerWithEmail } = useAuth();

  // 自定义钱包连接处理
  const handleWalletConnect = async () => {
    if (!connected) {
      setWalletModalVisible(true);
    }
  };

  // 选择特定钱包并连接
  const handleSelectWallet = async (walletName) => {
    try {
      setLoading(true);
      select(walletName);
      setWalletModalVisible(false);
      // 连接会在钱包选择后自动触发
    } catch (error) {
      console.error('钱包连接失败:', error);
      message.error('钱包连接失败');
    } finally {
      setLoading(false);
    }
  };

  const handleWalletLogin = async () => {
    if (!connected || !publicKey) {
      message.error('请先连接钱包');
      return;
    }

    setLoading(true);
    try {
      await loginWithWallet();
      message.success('钱包登录成功！');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('钱包登录失败:', error);
      message.error(error.message || '钱包登录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTraditionalLogin = async (values) => {
    setLoading(true);
    try {
      await loginWithCredentials(values.username, values.password);
      message.success('登录成功！');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('登录失败:', error);
      message.error(error.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values) => {
    setLoading(true);
    try {
      await registerWithEmail(values.username, values.email, values.password, values.invite_code);
      message.success('注册成功！');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('注册失败:', error);
      message.error(error.message || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    loginForm.resetFields();
    registerForm.resetFields();
    onClose();
  };

  const tabItems = [
    {
      key: 'signin',
      label: 'Sign in',
      children: (
        <div style={{ padding: '10px 0' }}>
          {/* 钱包登录区域 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              marginBottom: 16,
              width: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Button
                type="primary"
                size="large"
                block
                onClick={handleWalletConnect}
                loading={loading}
                icon={<WalletOutlined style={{ strokeWidth: 1.5 }} />}
                style={{
                  backgroundColor: '#FF3314',
                  borderColor: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%'
                }}
              >
                {connected ? `已连接: ${wallet?.adapter.name}` : 'Select Wallet'}
              </Button>
            </div>

            {connected && (
              <div style={{ marginBottom: 16, textAlign: 'center' }}>
                <Text type="success" style={{ fontSize: '14px' }}>
                  钱包已连接: {publicKey?.toString().slice(0, 8)}...{publicKey?.toString().slice(-8)}
                </Text>
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    size="large"
                    block
                    loading={loading}
                    onClick={handleWalletLogin}
                    style={{
                      backgroundColor: '#FF3314',
                      borderColor: '#FF3314',
                      borderRadius: '8px',
                      height: '48px'
                    }}
                  >
                    签名登录
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* OR 分隔线 */}
          <div style={{
            textAlign: 'center',
            margin: '24px 0',
            position: 'relative'
          }}>
            <div style={{
              height: '1px',
              backgroundColor: '#e8e8e8',
              position: 'relative'
            }}>
              <span style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'white',
                padding: '0 16px',
                color: '#999',
                fontSize: '14px'
              }}>
                OR
              </span>
            </div>
          </div>

          {/* 邮箱登录区域 */}
          <div>
            <Form
              form={loginForm}
              layout="vertical"
              onFinish={handleTraditionalLogin}
              autoComplete="off"
            >
              <Form.Item
                name="username"
                label="Email/Username"
                rules={[
                  { required: true, message: '请输入邮箱或用户名' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Email or Username"
                  size="large"
                  style={{ borderRadius: '8px', height: '48px' }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                  size="large"
                  style={{ borderRadius: '8px', height: '48px' }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={loading}
                  style={{
                    backgroundColor: '#FF3314',
                    borderColor: '#FF3314',
                    borderRadius: '8px',
                    height: '48px',
                    fontSize: '16px'
                  }}
                >
                  Continue
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      )
    },
    {
      key: 'signup',
      label: 'Sign up',
      children: (
        <div style={{ padding: '20px 0' }}>
          {/* 钱包注册区域 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              marginBottom: 16,
              width: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Button
                type="primary"
                size="large"
                block
                onClick={handleWalletConnect}
                loading={loading}
                icon={<WalletOutlined style={{ strokeWidth: 1.5 }} />}
                style={{
                  backgroundColor: '#FF3314',
                  borderColor: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%'
                }}
              >
                {connected ? `已连接: ${wallet?.adapter.name}` : 'Select Wallet'}
              </Button>
            </div>

            {connected && (
              <div style={{ marginBottom: 16, textAlign: 'center' }}>
                <Text type="success" style={{ fontSize: '14px' }}>
                  钱包已连接: {publicKey?.toString().slice(0, 8)}...{publicKey?.toString().slice(-8)}
                </Text>
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    size="large"
                    block
                    loading={loading}
                    onClick={handleWalletLogin}
                    style={{
                      backgroundColor: '#FF3314',
                      borderColor: '#FF3314',
                      borderRadius: '8px',
                      height: '48px'
                    }}
                  >
                    使用钱包注册
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* OR 分隔线 */}
          <div style={{
            textAlign: 'center',
            margin: '24px 0',
            position: 'relative'
          }}>
            <div style={{
              height: '1px',
              backgroundColor: '#e8e8e8',
              position: 'relative'
            }}>
              <span style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'white',
                padding: '0 16px',
                color: '#999',
                fontSize: '14px'
              }}>
                OR
              </span>
            </div>
          </div>

          {/* 邮箱注册区域 */}
          <div>
            <Form
              form={registerForm}
              layout="vertical"
              onFinish={handleRegister}
              autoComplete="off"
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Email"
                  size="large"
                  style={{ borderRadius: '8px', height: '48px' }}
                />
              </Form.Item>

              <Form.Item
                name="username"
                label="Username"
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 3, message: '用户名至少3个字符' },
                  { pattern: /^[a-zA-Z0-9_]+$/, message: '用户名只能包含字母、数字和下划线' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Username"
                  size="large"
                  style={{ borderRadius: '8px', height: '48px' }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                  size="large"
                  style={{ borderRadius: '8px', height: '48px' }}
                />
              </Form.Item>

              <Form.Item
                name="invite_code"
                label="Invite Code"
                rules={[
                  { required: true, message: '请输入邀请码' }
                ]}
              >
                <Input
                  placeholder="Enter your invite code"
                  size="large"
                  style={{ borderRadius: '8px', height: '48px' }}
                />
              </Form.Item>

              <div style={{
                fontSize: '12px',
                color: '#999',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                可以使用邀请码: <code style={{
                  backgroundColor: '#f5f5f5',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}>SCAI_250923</code>
              </div>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={loading}
                  style={{
                    backgroundColor: '#FF3314',
                    borderColor: '#FF3314',
                    borderRadius: '8px',
                    height: '48px',
                    fontSize: '16px'
                  }}
                >
                  Continue
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      )
    }

  ];

  return (
    <>

      <Modal
        title={null}
        open={visible}
        onCancel={handleCancel}
        footer={null}
        width={450}
        centered
        destroyOnClose
        styles={{
          body: { padding: '32px 24px' }
        }}
      >

        <div style={{
          textAlign: 'center',
          marginBottom: '24px',
          marginTop: '-8px'
        }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            lineHeight: '1.2',
            marginBottom: '4px',
            letterSpacing: '1px'
          }}>
            SCAI
          </div>
          <div style={{
            fontSize: '16px',
            color: '#666',
            lineHeight: '1.3',
            fontWeight: '400'
          }}>
            Web3 Brain of Science.
          </div>
        </div>
        <Tabs
          defaultActiveKey="signin"
          items={tabItems}
          centered
          size="large"
          tabBarStyle={{
            marginBottom: '32px',
            borderBottom: '1px solid #e8e8e8',
            fontSize: '18px',
            fontWeight: '500'
          }}
        />
      </Modal>

      {/* 钱包选择弹窗 */}
      <Modal
        title="选择钱包"
        open={walletModalVisible}
        onCancel={() => setWalletModalVisible(false)}
        footer={null}
        width={400}
        centered
      >
        <div style={{ padding: '20px 0' }}>
          {wallets.map((walletAdapter) => (
            <Button
              key={walletAdapter.adapter.name}
              type="text"
              size="large"
              block
              onClick={() => handleSelectWallet(walletAdapter.adapter.name)}
              style={{
                height: '56px',
                marginBottom: '12px',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '0 16px',
                textAlign: 'left'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img
                  src={walletAdapter.adapter.icon}
                  alt={walletAdapter.adapter.name}
                  style={{ width: '32px', height: '32px' }}
                />
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '500' }}>
                    {walletAdapter.adapter.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {walletAdapter.readyState === 'Installed' ? '已安装' : '未安装'}
                  </div>
                </div>
              </div>
            </Button>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default LoginModal;